import {
   CallEnd,
   Chat,
   Close,
   Fullscreen,
   FullscreenExit,
   Hd,
   Logout,
   Mic,
   MicOff,
   QueueMusic,
   Settings,
   Videocam,
   VideocamOff,
   Visibility,
 } from "@mui/icons-material";
 import {
   Avatar,
   Box,
   Button,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   IconButton,
   ListItemIcon,
   ListItemText,
   Menu,
   MenuItem,
   Paper,
   Snackbar,
   styled,
   Tooltip,
   TooltipProps,
   Typography,
   useMediaQuery,
   useTheme,
 } from "@mui/material";
  import {
    LocalUser,
    RemoteUser,
    useJoin,
    useLocalCameraTrack,
    useLocalMicrophoneTrack,
    usePublish,
    useRemoteAudioTracks,
    useRemoteUsers,
    useRTCClient,
  } from "agora-rtc-react";
  import AgoraRTM from "agora-rtm-sdk";
  import { enqueueSnackbar } from "notistack";
  import { ReactNode, useEffect, useRef, useState } from "react";
 
  import { AGORA_APP_ID, AGORA_RTM_APP_ID } from "../../../../constants/config";
  import { useCurrentTalentProfile } from "../../../../hooks";
  import { getAgoraToken } from "../../../../services/api/agora.api";

  import AgoraWrapper from "../AgoraWrapper";
  // import VideoCallWithChat from "../../../Chat/Chat";
  import AudioMix from "./AudioMix";
  
  const VideoCallWrapper = styled(Box)<{
    isChatOpen: boolean;
    isFullscreen: boolean;
  }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    width: 100%;
    transition: width 0.3s ease-in-out;
    ${(props) =>
      props.isFullscreen
        ? `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 9999;
      background-color: #000;
      padding: 0;
    `
        : ""}
  
    .chat-container {
      opacity: ${(props) => (props.isChatOpen ? 1 : 0)};
      transition: opacity 0.3s ease;
    }
  `;
  
  function NewKaraokeCardLiveStreamContent({
    userId,
    videoId,
    onCallEnd,
    displayName,
    username,
    role = "audience",
    qrCodeClickHandler,
    flashCardId,
    onRequestClick,
    profileImage,
    sessionEnded = false,
  }: {
    userId: string;
    videoId: string;
    onCallEnd: () => void;
    displayName: string;
    username?: string;
    role?: "host" | "audience";
    qrCodeClickHandler?: () => void;
    flashCardId?: string | null;
    onRequestClick?: () => void;
    profileImage?: string;
    sessionEnded?: boolean;
  }) {
    const [micOn, setMic] = useState(true);
    const [cameraOn, setCamera] = useState(true);
    const { localMicrophoneTrack } = useLocalMicrophoneTrack(
      role === "host" && micOn
    );
  
    const { localCameraTrack } = useLocalCameraTrack(role === "host");
    const [activeConnection, setActiveConnection] = useState(true);
    const [streamToken, setStreamToken] = useState<string | null>(null);
    const remoteUsers = useRemoteUsers();
    const [openChat, setOpenChat] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [videoQuality, setVideoQuality] = useState<
      "240p" | "360p" | "480p" | "720p" | "1080p"
    >("1080p");
    const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(
      null
    );
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success" as "success" | "warning" | "error",
    });
   
    // RTM for viewer count and chat
    const [rtmMembers, setRtmMembers] = useState<string[]>([]);
    const rtmClientRef = useRef<any>(null);
    const rtmChannelRef = useRef<any>(null);
    const rtmLoginStateRef = useRef<
      "idle" | "logging-in" | "logged-in" | "logging-out"
    >("idle");
    const rtmSetupInProgressRef = useRef(false);
    const [isRTMChannelJoined, setIsRTMChannelJoined] = useState(false);
  
    // Agora RTC Client
    const client = useRTCClient();
  
    // Track channel join state
    const [isChannelJoined, setIsChannelJoined] = useState(false);
  
    // Fullscreen functionality
    const [isFullscreen, setIsFullscreen] = useState(false);
  
    // End call confirmation dialog state
    const [showEndCallDialog, setShowEndCallDialog] = useState(false);

    // Audio mix panel state (host only)
    const [isAudioMixOpen, setIsAudioMixOpen] = useState(false);
  
    // Get current talent profile for talent-id header
    const { currentProfile } = useCurrentTalentProfile();
  
    useEffect(() => {
      if (role !== "host" || !localCameraTrack) return;
      localCameraTrack
        .setEnabled(cameraOn)
        .catch((err: any) => console.error("Failed to toggle camera:", err));
    }, [role, localCameraTrack, cameraOn]);

    useEffect(() => {
      if (role === "host" && localCameraTrack) {
        localCameraTrack.setEncoderConfiguration("1080p");
      }
    }, [role, localCameraTrack]);
  
    // Setup RTM for viewer count and chat
    useEffect(() => {
      if (!AGORA_RTM_APP_ID || !userId || !videoId) return;
  
      // Prevent multiple simultaneous setup attempts
      if (rtmSetupInProgressRef.current) {
        console.log("[RTM] Setup already in progress, skipping...");
        return;
      }
  
      const setupRTM = async () => {
        // Check if already logged in and channel joined
        if (
          rtmClientRef.current &&
          rtmChannelRef.current &&
          rtmLoginStateRef.current === "logged-in"
        ) {
          console.log(
            "[RTM] Client already logged in and channel joined, skipping setup"
          );
          setIsRTMChannelJoined(true);
          return;
        }
  
        // Check if login is in progress
        if (rtmLoginStateRef.current === "logging-in") {
          console.log("[RTM] Login already in progress, skipping setup");
          return;
        }
  
        rtmSetupInProgressRef.current = true;
  
        try {
          // Cleanup existing client if it exists but is not logged in
          if (rtmClientRef.current && rtmLoginStateRef.current !== "logged-in") {
            try {
              if (rtmChannelRef.current) {
                await rtmChannelRef.current.leave().catch(() => {});
                rtmChannelRef.current = null;
              }
              if (rtmClientRef.current) {
                await rtmClientRef.current.logout().catch(() => {});
                rtmClientRef.current = null;
              }
            } catch (cleanupError) {
              console.warn("[RTM] Cleanup error (ignoring):", cleanupError);
            }
          }
  
          // Create new RTM client instance
          const rtmClient = AgoraRTM.createInstance(AGORA_RTM_APP_ID);
  
          // Set login state to logging-in
          rtmLoginStateRef.current = "logging-in";
  
          // Login with proper error handling
          try {
            await rtmClient.login({ uid: userId });
            rtmLoginStateRef.current = "logged-in";
            console.log("[RTM] ✓ Successfully logged in with uid:", userId);
          } catch (loginError: any) {
            // Handle Error Code 8 specifically - this means already logged in
            if (loginError?.errorCode === 8 || loginError?.code === 8) {
              console.warn(
                "[RTM] Error Code 8: SDK is already logged in or login in progress."
              );
              console.warn(
                "[RTM] This is usually safe - client may already be authenticated."
              );
  
              // Check if we have an existing logged-in client
              if (rtmClientRef.current) {
                console.log("[RTM] Using existing RTM client instance");
                rtmLoginStateRef.current = "logged-in";
              } else {
                // Wait a moment and mark as logged in to proceed
                await new Promise((resolve) => setTimeout(resolve, 500));
                rtmLoginStateRef.current = "logged-in";
                console.log(
                  "[RTM] Assuming logged in state and proceeding with channel setup"
                );
              }
            } else {
              // For other errors, log and stop retrying
              console.error("[RTM] Login failed with error:", loginError);
              console.error(
                "[RTM] Error Code:",
                loginError?.errorCode || loginError?.code
              );
              console.error("[RTM] Error Message:", loginError?.message);
              rtmLoginStateRef.current = "idle";
              rtmSetupInProgressRef.current = false;
              // Don't throw - RTM is optional for MediaPush, just log the error
              console.warn(
                "[RTM] RTM setup failed but continuing (RTM is optional for MediaPush)"
              );
              return;
            }
          }
  
          // Create and join channel
          const rtmChannel = rtmClient.createChannel(videoId);
  
          try {
            await rtmChannel.join();
            setIsRTMChannelJoined(true);
            console.log("[RTM] ✓ Successfully joined RTM channel:", videoId);
          } catch (channelError: any) {
            console.error("[RTM] ✗ Failed to join RTM channel:", channelError);
            console.error(
              "[RTM] Channel Error Code:",
              channelError?.errorCode || channelError?.code
            );
            console.error("[RTM] Channel Error Message:", channelError?.message);
  
            // If channel join fails, try to logout to clean up (but don't block)
            try {
              await rtmClient.logout().catch(() => {});
            } catch (logoutError) {
              console.warn(
                "[RTM] Failed to logout after channel join error (non-critical):",
                logoutError
              );
            }
            rtmLoginStateRef.current = "idle";
            setIsRTMChannelJoined(false);
            rtmSetupInProgressRef.current = false;
            // Don't return - RTM is optional, continue without it
            console.warn(
              "[RTM] RTM channel join failed but continuing (RTM is optional for MediaPush)"
            );
            return;
          }
  
          const updateMembers = async () => {
            try {
              const members = await rtmChannel.getMembers();
              setRtmMembers(members);
            } catch (err) {
              console.error("[RTM] Failed to get members:", err);
            }
          };
  
          rtmChannel.on("MemberJoined", async (memberId) => {
            // Optimistic local update so host count changes immediately.
            setRtmMembers((prev) =>
              prev.includes(memberId) ? prev : [...prev, memberId]
            );
            try {
              const members = await rtmChannel.getMembers();
              setRtmMembers(members);
            } catch (err) {
              console.error("[RTM] Failed to update members on join:", err);
            }
          });
  
          rtmChannel.on("MemberLeft", async (memberId) => {
            // Optimistic local update so host count changes immediately.
            setRtmMembers((prev) => prev.filter((id) => id !== memberId));
            try {
              const members = await rtmChannel.getMembers();
              setRtmMembers(members);
            } catch (err) {
              console.error("[RTM] Failed to update members on leave:", err);
            }
          });
  
          // Initial member count
          await updateMembers();
  
          // Store references
          rtmClientRef.current = rtmClient;
          rtmChannelRef.current = rtmChannel;
          setIsRTMChannelJoined(true);
  
          console.log(
            "[RTM] ✓ Setup completed successfully - RTM client logged in and channel joined"
          );
        } catch (error: any) {
          console.error("[RTM] ✗ Setup failed:", error);
          rtmLoginStateRef.current = "idle";
          setIsRTMChannelJoined(false);
  
          // Log error details for debugging
          if (error?.errorCode || error?.code) {
            console.error("[RTM] Error Code:", error.errorCode || error.code);
          }
          if (error?.message) {
            console.error("[RTM] Error Message:", error.message);
          }
  
          // For Error Code 8, stop retrying to prevent infinite loop
          if (error?.errorCode === 8 || error?.code === 8) {
            console.warn(
              "[RTM] Error Code 8 detected - stopping RTM setup to prevent reconnection loop"
            );
            console.warn(
              "[RTM] RTM is optional for MediaPush - continuing without RTM"
            );
          }
        } finally {
          rtmSetupInProgressRef.current = false;
        }
      };
  
      setupRTM();
  
      return () => {
        const cleanup = async () => {
          rtmSetupInProgressRef.current = false;
  
          try {
            if (rtmLoginStateRef.current === "logging-out") {
              console.log("[RTM] Cleanup already in progress");
              return;
            }
  
            rtmLoginStateRef.current = "logging-out";
  
            if (rtmChannelRef.current) {
              try {
                await rtmChannelRef.current.leave();
                setIsRTMChannelJoined(false);
                console.log("[RTM] Channel left successfully");
              } catch (err) {
                console.warn("[RTM] Error leaving channel:", err);
                setIsRTMChannelJoined(false);
              }
              rtmChannelRef.current = null;
            }
  
            if (rtmClientRef.current) {
              try {
                await rtmClientRef.current.logout();
                console.log("[RTM] Logged out successfully");
              } catch (err) {
                console.warn("[RTM] Error logging out:", err);
              }
              rtmClientRef.current = null;
            }
  
            rtmLoginStateRef.current = "idle";
            setIsRTMChannelJoined(false);
          } catch (err) {
            console.error("[RTM] Cleanup failed:", err);
            rtmLoginStateRef.current = "idle";
          }
        };
        cleanup();
      };
    }, [userId, videoId]);
  
    // Calculate viewer count (excluding host)
    const realTimeRTMAudience =
      rtmMembers.filter((item) => !isNaN(Number(item))) || [];
    const realTimeRTMAudienceCount = realTimeRTMAudience.length - 1;
  
    console.log(realTimeRTMAudience, "realTimeRTMAudience");
    console.log(realTimeRTMAudienceCount, "realTimeRTMAudienceCount");
    console.log(username, "username");
  
    const toggleFullscreen = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
        setIsFullscreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setIsFullscreen(false);
        }
      }
    };
  
    // Listen for fullscreen changes
    useEffect(() => {
      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };
  
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.addEventListener("mozfullscreenchange", handleFullscreenChange);
      document.addEventListener("MSFullscreenChange", handleFullscreenChange);
  
      return () => {
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
        document.removeEventListener(
          "webkitfullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "mozfullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "MSFullscreenChange",
          handleFullscreenChange
        );
      };
    }, []);
  
    const handleQualityChange = async (
      quality: "240p" | "360p" | "480p" | "720p" | "1080p"
    ) => {
      if (localCameraTrack) {
        try {
          await localCameraTrack.setEncoderConfiguration(quality);
          setVideoQuality(quality);
          setSnackbar({
            open: true,
            message: `Video quality changed to ${quality}`,
            severity: "success",
          });
        } catch {
          setSnackbar({
            open: true,
            message: "Failed to change video quality",
            severity: "error",
          });
        }
      }
      setSettingsAnchor(null);
    };
  
    // Close any open panels/menus when another button is clicked
    const closePanels = (except?: "settings" | "chat" | "audioMix") => {
      if (except !== "settings") setSettingsAnchor(null);
      if (except !== "chat") setOpenChat(false);
      if (except !== "audioMix") setIsAudioMixOpen(false);
    };
  
    const controlButtonStyle = {
      height: { xs: "36px", sm: "42px" },
      width:  { xs: "36px", sm: "42px" },
      padding: "8px",
      backgroundColor: "rgba(238,242,255,0.92)",
      color: "#1a1a1a",
      borderRadius: "50%",
      zIndex: 3,
      transition: "background-color 0.2s",
      "&:hover": { backgroundColor: "#fff" },
    };
  
    const controlButtonStyleDestructive = {
      height: { xs: "36px", sm: "42px" },
      width:  { xs: "36px", sm: "42px" },
      padding: "8px",
      backgroundColor: "#E50914",
      color: "#fff",
      borderRadius: "50%",
      zIndex: 3,
      transition: "background-color 0.2s",
      "&:hover": { backgroundColor: "#b20710" },
    };
  
    useEffect(() => {
      if (streamToken) return;
      const token = localStorage.getItem("diamondAgoraToken");
      if (token) {
        setStreamToken(token);
      } else {
        getAgoraToken(videoId + "", userId.toString())
          .then(setStreamToken)
          .catch((err: any) =>
            enqueueSnackbar(err.message || "Failed to fetch token", {
              variant: "error",
            })
          );
      }
    }, [videoId, streamToken, userId]);
    console.log("talent Id ", userId);
    console.log(streamToken, "streamToken");
  
    useJoin(
      {
        appid: AGORA_APP_ID!,
        channel: videoId!,
        token: streamToken,
        uid: userId,
      },
      !!(streamToken && userId && AGORA_APP_ID && videoId && activeConnection)
    );
    console.log("useJoin talent Id ", userId);
    // Only publish if user is host
    usePublish([localMicrophoneTrack, localCameraTrack]);

  
    const { audioTracks } = useRemoteAudioTracks(remoteUsers);
    audioTracks.forEach((track) => track.play());
  
    const handleEndCallClick = () => {
      setShowEndCallDialog(true);
    };
  
    const handleEndCallCancel = () => {
      setShowEndCallDialog(false);
    };
  
    const handleEndCall = async () => {
      setShowEndCallDialog(false);
      try {
      setActiveConnection(false);

        // Cleanup RTM with proper state management
        if (
          rtmLoginStateRef.current !== "logging-out" &&
          rtmLoginStateRef.current !== "idle"
        ) {
          rtmLoginStateRef.current = "logging-out";
  
          // First leave the RTM channel
          if (rtmChannelRef.current) {
            try {
              await rtmChannelRef.current.leave();
              console.log("[RTM] Channel left on call end");
            } catch (err) {
              console.warn("[RTM] Error leaving channel on call end:", err);
            }
            rtmChannelRef.current = null;
          }
  
          // Then log out from RTM
          if (rtmClientRef.current) {
            try {
              await rtmClientRef.current.logout();
              console.log("[RTM] Logged out on call end");
            } catch (err) {
              console.warn("[RTM] Error logging out on call end:", err);
            }
            rtmClientRef.current = null;
          }
  
          rtmLoginStateRef.current = "idle";
        }
  
        // Optional: remove yourself locally to ensure count updates instantly
        setRtmMembers((prev) => prev.filter((id) => id !== userId));
  
        onCallEnd?.();
      } catch (error) {
        console.error("Error leaving call:", error);
        rtmLoginStateRef.current = "idle";
      }
    };
  
    // Generate shareable link
    console.log(remoteUsers, "remoteUsersremoteUsers");
  
    return (
      <VideoCallWrapper
        p={isFullscreen ? 0 : 2}
        isChatOpen={openChat}
        isFullscreen={isFullscreen}
      >
        {/* Main Video Section */}
        <Paper
          elevation={isFullscreen ? 0 : 3}
          sx={{
            width: "100%",
            height: isFullscreen ? "100vh" : isMobile ? "50vh" : "80vh",
            position: "relative",
          }}
        >
          {/* needed only if suit is Diamond, so commenting for now */}
          {/* live diamond session has been removed so no need to show linked products for now*/}
          {/* <LinkedProducts /> */}
  
          {role === "host" ? (
            // Show local video for host only when camera is on.
            // Remote publish behavior is unchanged; this only controls self preview.
            cameraOn && localCameraTrack ? (
              <LocalUser
                audioTrack={localMicrophoneTrack}
                videoTrack={localCameraTrack}
                cameraOn={cameraOn}
                micOn={micOn}
                playAudio={false}
                playVideo={true}
                style={{ height: "100%", width: "100%", pointerEvents: "none" }}
              />
            ) : (
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Blurred profile image as background */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    backgroundImage: currentProfile?.profile_image
                      ? `url(${currentProfile.profile_image})`
                      : undefined,
                    backgroundSize: "cover",
                    backgroundColor: !currentProfile?.profile_image
                      ? "#1a1a2e"
                      : "transparent",
                    backgroundPosition: "center",
                    filter: "blur(16px)",
                    transform: "scale(1.1)",
                  }}
                />
                {/* Dimming overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    background: "#000",
                    opacity: 0.35,
                    zIndex: 1,
                  }}
                />
                {/* Circular profile image / avatar */}
                {currentProfile?.profile_image ? (
                  <img
                    src={currentProfile.profile_image}
                    alt="Profile"
                    style={{
                      width: "90px",
                      height: "90px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      position: "relative",
                      zIndex: 2,
                      border: "3px solid rgba(255,255,255,0.6)",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 90,
                      height: 90,
                      bgcolor: "#FBBF24",
                      fontWeight: 600,
                      fontSize: 32,
                      position: "relative",
                      zIndex: 2,
                      border: "3px solid rgba(255,255,255,0.6)",
                    }}
                  >
                    {(displayName || username || "H").charAt(0).toUpperCase()}
                  </Avatar>
                )}
              </Box>
            )
          ) : remoteUsers.length > 0 ? (
            // Show first remote user (host) for audience
            remoteUsers[0]?.hasVideo ? (
            <RemoteUser
              user={remoteUsers[0]}
              style={{ height: "100%", width: "100%", pointerEvents: "none" }}
            />
          ) : (
              // Host has camera off — show their profile image
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Blurred profile image as background */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    backgroundImage: profileImage
                      ? `url(${profileImage})`
                      : undefined,
                    backgroundSize: "cover",
                    backgroundColor: !profileImage ? "#1a1a2e" : "transparent",
                    backgroundPosition: "center",
                    filter: "blur(16px)",
                    transform: "scale(1.1)",
                  }}
                />
                {/* Dimming overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    background: "#000",
                    opacity: 0.35,
                    zIndex: 1,
                  }}
                />
                {/* Circular profile image / avatar */}
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Host Profile"
                    style={{
                      width: "90px",
                      height: "90px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      position: "relative",
                      zIndex: 2,
                      border: "3px solid rgba(255,255,255,0.6)",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 90,
                      height: 90,
                      bgcolor: "#FBBF24",
                      fontWeight: 600,
                      fontSize: 32,
                      position: "relative",
                      zIndex: 2,
                      border: "3px solid rgba(255,255,255,0.6)",
                    }}
                  >
                    {(displayName || "H").charAt(0).toUpperCase()}
                  </Avatar>
                )}
              </Box>
            )
          ) : (
            // No remote users yet — host hasn't joined / temporarily disconnected.
            // Show profile image background instead of a plain black screen.
            <Box
              sx={{
                height: "100%",
                width: "100%",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Blurred profile image as background */}
              <Box
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  backgroundImage: profileImage
                    ? `url(${profileImage})`
                    : undefined,
                  backgroundSize: "cover",
                  backgroundColor: !profileImage ? "#1a1a2e" : "transparent",
                  backgroundPosition: "center",
                  filter: "blur(16px)",
                  transform: "scale(1.1)",
                }}
              />
              {/* Dimming overlay */}
              <Box
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  background: "#000",
                  opacity: 0.5,
                  zIndex: 1,
                }}
              />
              {/* Circular profile image / avatar + waiting text */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Host Profile"
                    style={{
                      width: "90px",
                      height: "90px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid rgba(255,255,255,0.6)",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 90,
                      height: 90,
                      bgcolor: "#FBBF24",
                      fontWeight: 600,
                      fontSize: 32,
                      border: "3px solid rgba(255,255,255,0.6)",
                    }}
                  >
                    {(displayName || "H").charAt(0).toUpperCase()}
                  </Avatar>
                )}
              <Typography
                variant="h6"
                align="center"
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                }}
              >
                Waiting for host to join...
              </Typography>
              </Box>
            </Box>
          )}
  
          {/* Session-ended overlay — shown to audience when host ends the session */}
          {role === "audience" && sessionEnded && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 50,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                overflow: "hidden",
              }}
            >
              {/* Blurred profile image background */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: profileImage
                    ? `url(${profileImage})`
                    : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundColor: !profileImage ? "#1a1a2e" : "transparent",
                  filter: "blur(20px)",
                  transform: "scale(1.1)",
                }}
              />
              {/* Dark dimming overlay */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: "#000",
                  opacity: 0.6,
                }}
              />
              {/* Content */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  px: 3,
                  textAlign: "center",
                }}
              >
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Host Profile"
                    style={{
                      width: "90px",
                      height: "90px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid rgba(255,255,255,0.6)",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 90,
                      height: 90,
                      bgcolor: "#FBBF24",
                      fontWeight: 600,
                      fontSize: 32,
                      border: "3px solid rgba(255,255,255,0.6)",
                    }}
                  >
                    {(displayName || "H").charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <Typography
                  variant="h5"
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                  }}
                >
                  The talent has ended the session
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  Thank you for watching!
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
  
        {/* Viewer Count - Only show for host */}
        {role === "host" && (
          <Box
            sx={{
              position: "absolute",
              top: { xs: 16, md: 24 },
              left: { xs: 16, md: 24 },
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              borderRadius: "8px",
              padding: "8px 12px",
              zIndex: 100,
            }}
          >
            <Visibility sx={{ color: "#fff", fontSize: "20px" }} />
            <Typography
              variant="body1"
              sx={{
                color: "#fff",
                fontWeight: 600,
                fontSize: { xs: "14px", md: "16px" },
              }}
            >
              {realTimeRTMAudienceCount > 0 ? realTimeRTMAudienceCount : 0}{" "}
              {realTimeRTMAudienceCount === 1 ? "Viewer" : "Viewers"}
            </Typography>
          </Box>
        )}
  
        {/* Control Buttons - Different for host and audience */}
        <Box
          display="inline-flex"
          alignItems="center"
          gap={{ xs: 0.5, sm: 0.75 }}
          sx={{
            position: "absolute",
            bottom: { xs: 12, sm: 18 },
            left: "50%",
            transform: "translateX(-50%)",
            px: { xs: 1.25, sm: 1.75 },
            py: { xs: 0.75, sm: 1 },
            borderRadius: "999px",
            bgcolor: "rgba(10,10,10,0.75)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.13)",
            boxShadow: "0 6px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05) inset",
            whiteSpace: "nowrap",
            zIndex: 1000,
            pointerEvents: "auto",
          }}
        >
          {role === "host" ? (
            <>
              {/* mute button */}
              <VideoTooltip title={micOn ? "Mute" : "Unmute"} placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels(); setMic((prev) => !prev); }}
                    icon={micOn ? <Mic sx={{ fontSize: 18 }} /> : <MicOff sx={{ fontSize: 18 }} />}
                    sx={controlButtonStyle}
                  />
                </Box>
              </VideoTooltip>

              {/* camera button */}
              <VideoTooltip title={cameraOn ? "Turn off camera" : "Turn on camera"} placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels(); setCamera((prev) => !prev); }}
                    icon={cameraOn ? <Videocam sx={{ fontSize: 18 }} /> : <VideocamOff sx={{ fontSize: 18 }} />}
                    sx={controlButtonStyle}
                  />
                </Box>
              </VideoTooltip>

              {/* audio mix button */}
              <VideoTooltip title="Audio Mix" placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels("audioMix"); setIsAudioMixOpen((p) => !p); }}
                    icon={<QueueMusic sx={{ fontSize: 18 }} />}
                    sx={isAudioMixOpen ? { ...controlButtonStyle, backgroundColor: "#E50914", color: "#fff", "&:hover": { backgroundColor: "#C1121F" } } : controlButtonStyle}
                  />
                </Box>
              </VideoTooltip>

              {/* end stream button */}
              <VideoTooltip title="End Stream" placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels(); handleEndCallClick(); }}
                    icon={<CallEnd sx={{ fontSize: 18 }} />}
                    sx={controlButtonStyleDestructive}
                  />
                </Box>
              </VideoTooltip>

              {/* settings button */}
              <VideoTooltip title="Video Quality" placement="top" arrow>
                <IconButton
                  onClick={(e) => { closePanels("settings"); setSettingsAnchor(e.currentTarget as HTMLElement); }}
                  sx={controlButtonStyle}
                >
                  <Settings sx={{ fontSize: 18 }} />
                </IconButton>
              </VideoTooltip>

              {/* video quality menu */}
              <Menu
                anchorEl={settingsAnchor}
                open={Boolean(settingsAnchor)}
                onClose={() => setSettingsAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                sx={{ zIndex: 10000 }}
              >
                {(["240p", "360p", "480p", "720p", "1080p"] as const).map((q) => (
                  <MenuItem key={q} onClick={() => handleQualityChange(q)}>
                    <ListItemIcon><Hd fontSize="small" /></ListItemIcon>
                    <ListItemText primary={q} secondary={videoQuality === q ? "Current" : ""} />
                  </MenuItem>
                ))}
              </Menu>

              <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              />

              {/* fullscreen button */}
              <VideoTooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"} placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels(); toggleFullscreen(); }}
                    icon={isFullscreen ? <FullscreenExit sx={{ fontSize: 18 }} /> : <Fullscreen sx={{ fontSize: 18 }} />}
                    sx={controlButtonStyle}
                  />
                </Box>
              </VideoTooltip>

              {/* chat button */}
              <VideoTooltip title={openChat ? "Close Chat" : "Open Chat"} placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels("chat"); setOpenChat((p) => !p); }}
                    icon={<Chat sx={{ fontSize: 18 }} />}
                    sx={controlButtonStyle}
                  />
                </Box>
              </VideoTooltip>
            </>
          ) : (
            <>
              <VideoTooltip title="Leave Stream" placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels(); handleEndCallClick(); }}
                    icon={<Logout sx={{ fontSize: 18 }} />}
                    sx={controlButtonStyleDestructive}
                  />
                </Box>
              </VideoTooltip>

              {/* fullscreen button */}
              <VideoTooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"} placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels(); toggleFullscreen(); }}
                    icon={isFullscreen ? <FullscreenExit sx={{ fontSize: 18 }} /> : <Fullscreen sx={{ fontSize: 18 }} />}
                    sx={controlButtonStyle}
                  />
                </Box>
              </VideoTooltip>

              {/* chat button */}
              <VideoTooltip title={openChat ? "Close Chat" : "Open Chat"} placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={() => { closePanels("chat"); setOpenChat((p) => !p); }}
                    icon={<Chat sx={{ fontSize: 18 }} />}
                    sx={controlButtonStyle}
                  />
                </Box>
              </VideoTooltip>
            </>
          )}
        </Box>
  
        {/* Audio Mix Panel — host only */}
        {role === "host" && (
          <AudioMix
            open={isAudioMixOpen}
            onClose={() => setIsAudioMixOpen(false)}
          />
        )}

        {/* Chat Window */}
        {/* {AGORA_RTM_APP_ID && videoId && userId && (
          <VideoCallWithChat
            appId={AGORA_RTM_APP_ID}
            channelName={videoId}
            userId={username || userId || "Guest"}
            isGroupSession={true}
          />
        )} */}
  
        {/* End Call Confirmation Dialog */}
        <Dialog
          open={showEndCallDialog}
          onClose={handleEndCallCancel}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" component="div" fontWeight={700}>
              {role === "host" ? "End Stream?" : "Leave Stream?"}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {role === "host"
                ? "This will disconnect all viewers and stop the stream."
                : "Are you sure you want to leave?"}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={handleEndCallCancel} variant="outlined" color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleEndCall}
              variant="contained"
              color="error"
              startIcon={<CallEnd />}
            >
              {role === "host" ? "End Stream" : "Leave"}
            </Button>
          </DialogActions>
        </Dialog>
      </VideoCallWrapper>
    );
  }
  
  export default function NewKaraokeCard(props: {
    userId: string;
    videoId: string;
    onCallEnd: () => void;
    displayName: string;
    username?: string;
    role?: "host" | "audience";
    qrCodeClickHandler?: () => void;
    flashCardId?: string | null;
    onRequestClick?: () => void;
    profileImage?: string;
    sessionEnded?: boolean;
  }) {
    return (
      <AgoraWrapper mode="live" role={props.role}>
        <NewKaraokeCardLiveStreamContent {...props} />
      </AgoraWrapper>
    );
  }
  /** Renders tooltip inline (disablePortal) so it stays visible in fullscreen mode. */
  const VideoTooltip = (props: TooltipProps) => (
    <Tooltip
      {...props}
      PopperProps={{ ...(props.PopperProps ?? {}), disablePortal: true }}
    />
  );
  
  const ControlButton = ({
    onClick,
    icon,
    sx = {},
    disabled = false,
  }: {
    onClick: () => void;
    icon: ReactNode;
    sx?: Record<string, any>;
    disabled?: boolean;
  }) => {
    return (
      <IconButton
        color="error"
        onClick={onClick}
        disabled={disabled}
        sx={{
          ...sx,
          pointerEvents: "auto",
          cursor: disabled ? "not-allowed" : "pointer",
          position: "relative",
          zIndex: sx?.zIndex || 1001,
        }}
      >
        {icon}
      </IconButton>
    );
  };
  